// src/utils/editorHelpers.js
import { extractHashtags } from "../extensions/Hashtag";
import { hashtagStore } from "../stores/hashtagStore";
import logger from "./logger";

// Store debounce timeout to clear between calls
let debounceTimeout;

/**
 * Synchronizes editor content with block data using debounce
 *
 * @param {Object} editor - TipTap editor instance
 * @param {Object} block - Block data object
 * @param {Function} dispatch - Svelte dispatch function
 * @param {Array} currentHashtags - Current hashtags array
 * @param {Function} updateHashtags - Callback to update hashtags array
 */
export function syncContentWithDebounce(
  editor,
  block,
  dispatch,
  currentHashtags,
  updateHashtags,
) {
  clearTimeout(debounceTimeout);
  debounceTimeout = setTimeout(() => {
    if (editor) {
      const content = editor.getHTML();
      const jsonContent = editor.getJSON();

      // Extract hashtags from content
      const extractedHashtags = extractHashtagsFromEditor(editor);
      const hashtagLabels = extractedHashtags.map((tag) => tag.label);

      // Update the local state via callback
      if (updateHashtags) {
        updateHashtags(hashtagLabels);
      }

      // Update the hashtag store
      hashtagStore.updateBlockHashtags(block.id, hashtagLabels);

      dispatch("contentUpdate", {
        html: content,
        json: jsonContent,
        type: block.type || "paragraph",
        hashtags: hashtagLabels,
      });
    }
  }, 300);
}

/**
 * Extracts hashtags from the editor content
 *
 * @param {Object} editor - TipTap editor instance
 * @returns {Array} Array of hashtag objects with { id, label, position }
 */
export function extractHashtagsFromEditor(editor) {
  return extractHashtags(editor);
}

/**
 * Checks if editor content is empty
 *
 * @param {Object} editor - TipTap editor instance
 * @returns {boolean} True if editor is empty
 */
export function isEditorEmpty(editor) {
  if (!editor) return true;
  return editor.isEmpty;
}

/**
 * Transforms a block to a different type
 *
 * @param {Object} editor - TipTap editor instance
 * @param {string} type - The block type to transform to
 * @param {string} blockId - The ID of the block being transformed
 * @param {Function} dispatch - Svelte dispatch function
 */
export function transformBlock(editor, type, blockId, dispatch) {
  if (!editor) return;

  switch (type) {
    case "heading1":
      editor.chain().focus().setHeading({ level: 1 }).run();
      break;
    case "heading2":
      editor.chain().focus().setHeading({ level: 2 }).run();
      break;
    case "heading3":
      editor.chain().focus().setHeading({ level: 3 }).run();
      break;
    case "bulletList":
      editor.chain().focus().toggleBulletList().run();
      break;
    case "orderedList":
      editor.chain().focus().toggleOrderedList().run();
      break;
    case "taskList":
      editor.chain().focus().toggleTaskList().run();
      break;
    case "blockquote":
      editor.chain().focus().toggleBlockquote().run();
      break;
    case "codeBlock":
      editor.chain().focus().toggleCodeBlock().run();
      break;
    default:
      editor.chain().focus().setParagraph().run();
  }

  dispatch("blockTypeChanged", { id: blockId, type });
}

/**
 * Inserts a link at the current selection or at cursor position
 *
 * @param {Object} editor - TipTap editor instance
 * @param {string} url - The URL to insert
 * @param {string} text - The link text (optional)
 */
export function insertLink(editor, url, text) {
  if (!editor) return;

  // If there's no selection, set the text from the url
  if (editor.state.selection.empty) {
    editor
      .chain()
      .focus()
      .insertContent(text || url)
      .setTextSelection({
        from: editor.state.selection.from - (text || url).length,
        to: editor.state.selection.from,
      })
      .setLink({ href: url })
      .run();
  } else {
    editor.chain().focus().setLink({ href: url }).run();
  }
}

/**
 * Handles clicks on hashtags with proper logging
 *
 * @param {string} hashtag - The hashtag that was clicked
 * @param {string} blockId - The ID of the block containing the hashtag
 * @param {Function} dispatch - Svelte dispatch function
 */
export function handleHashtagClick(hashtag, blockId, dispatch) {
  logger.debug("Hashtag clicked:", hashtag);
  dispatch("hashtagClick", {
    hashtag,
    blockId,
  });
}

/**
 * Focus editor at a specific position
 *
 * @param {Object} editor - TipTap editor instance
 * @param {string} blockId - Block ID for logging
 * @param {string} position - Position to focus ('start' or 'end')
 * @returns {boolean} Success status
 */
export function focusEditor(editor, blockId, position = "end") {
  if (editor) {
    logger.debug("Focusing editor", { blockId, position });
    editor.commands.focus(position === "end" ? "end" : "start", {
      scrollIntoView: false,
    });
    return true;
  }
  return false;
}

/**
 * Initialize hashtags from existing content
 *
 * @param {Object} editor - TipTap editor instance
 * @param {string} blockId - The block ID
 * @param {Function} updateHashtags - Callback to update hashtags array
 */
export function initializeHashtags(editor, blockId, updateHashtags) {
  if (!editor) return;

  setTimeout(() => {
    const extractedHashtags = extractHashtagsFromEditor(editor);
    const hashtagLabels = extractedHashtags.map((tag) => tag.label);

    // Update local state
    if (updateHashtags) {
      updateHashtags(hashtagLabels);
    }

    // Update the hashtag store with initial tags
    if (hashtagLabels.length > 0) {
      hashtagStore.updateBlockHashtags(blockId, hashtagLabels);
    }
  }, 200);
}
